/**
 * Cart Dropshipping Validator
 * Validates that dropshipping and non-dropshipping products are not mixed in the cart
 */

if (!customElements.get("cart-dropshipping-validator")) {
	class CartDropshippingValidator extends HTMLElement {
		constructor() {
			super();
			this.cartDrawer = document.querySelector("cart-drawer");
			this.mainCart = document.querySelector("cart-items");
			this.Notification = window.FoxTheme?.Notification;
			this.isResolvingConflict = false;
			this.lastCartSnapshot = new Map();
			this.snapshotMeta = {
				dropshippingQuantity: 0,
				regularQuantity: 0,
			};
			this.initialSnapshotPromise = this.syncCartSnapshot();
		}

		connectedCallback() {
			// Subscribe to cart update events
			this.cartUpdateUnsubscriber = window.FoxThemeEvents?.subscribe(
				PUB_SUB_EVENTS.cartUpdate,
				this.validateDropshippingMix.bind(this),
			);
		}

		disconnectedCallback() {
			if (this.cartUpdateUnsubscriber) {
				this.cartUpdateUnsubscriber();
			}
		}

		/**
		 * Fetches current cart state and stores snapshot for diffing
		 */
		async syncCartSnapshot() {
			try {
				const cart = await this.resolveCartState({});
				if (!cart || !cart.items || cart.items.length === 0) {
					this.storeCartSnapshot([]);
					return;
				}

				const itemsWithTags = await this.fetchProductTags(cart.items);
				this.storeCartSnapshot(itemsWithTags);
			} catch (error) {
				console.error("Error syncing cart snapshot:", error);
			}
		}

		/**
		 * Checks if a product has the dropshipping tag
		 * @param {Array} tags - Product tags array
		 * @returns {boolean}
		 */
		isDropshippingProduct(tags) {
			if (!Array.isArray(tags)) return false;
			return tags.some((tag) => tag === "dropshipping");
		}

		/**
		 * Validates if cart contains mixed dropshipping and non-dropshipping products
		 * @param {Object} response - Cart update response
		 */
		async validateDropshippingMix(response) {
			try {
				await this.initialSnapshotPromise;

				// Get current cart state (fall back to /cart.js when event payload lacks items)
				const cart = await this.resolveCartState(response);

				if (!cart || !cart.items || cart.items.length === 0) {
					this.storeCartSnapshot([]);
					return;
				}

				// Fetch full product data for each cart item to get tags
				const itemsWithTags = await this.fetchProductTags(cart.items);

				if (this.isResolvingConflict) {
					this.storeCartSnapshot(itemsWithTags);
					return;
				}

				// Separate dropshipping and non-dropshipping products
				const dropshippingItems = [];
				const regularItems = [];

				itemsWithTags.forEach((item) => {
					if (this.isDropshippingProduct(item.tags)) {
						dropshippingItems.push(item);
					} else {
						regularItems.push(item);
					}
				});

				const hasMix = dropshippingItems.length > 0 && regularItems.length > 0;

				if (hasMix) {
					const addedItems = this.identifyAddedItems(itemsWithTags, response);
					const newType = this.resolveNewItemType(
						dropshippingItems,
						regularItems,
					);
					const conflictingItems = this.selectConflictingItems(
						addedItems,
						newType,
						itemsWithTags,
						dropshippingItems,
						regularItems,
					);

					this.showMixedCartWarning(dropshippingItems, regularItems);

					if (conflictingItems.length) {
						this.isResolvingConflict = true;
						try {
							await this.revertConflictingItems(conflictingItems);
						} finally {
							this.isResolvingConflict = false;
						}
					}

					return;
				}

				// No mix detected, update snapshot for next comparison
				this.storeCartSnapshot(itemsWithTags);
			} catch (error) {
				this.isResolvingConflict = false;
				console.error("[Cart Dropshipping Validator] Error:", error);
			}
		}

		/**
		 * Fetches product tags for cart items
		 * @param {Array} items - Cart items
		 * @returns {Promise<Array>}
		 */
		async fetchProductTags(items) {
			const itemsWithTags = await Promise.all(
				items.map(async (item) => {
					try {
						// Fetch product data from handle
						const productHandle = item.handle;
						const response = await fetch(`/products/${productHandle}.js`);
						const productData = await response.json();

						return {
							...item,
							tags: productData.tags || [],
						};
					} catch (error) {
						console.error(`Error fetching tags for ${item.handle}:`, error);
						return {
							...item,
							tags: [],
						};
					}
				}),
			);

			return itemsWithTags;
		}

		/**
		 * Shows warning notification about mixed cart
		 * @param {Array} dropshippingItems - Dropshipping products
		 * @param {Array} regularItems - Regular products
		 */
		showMixedCartWarning(dropshippingItems, regularItems) {
			if (!this.Notification) {
				console.warn("Notification system not available");
				return;
			}

			const message = `
			<strong>Atenção!</strong><br> Você tem produtos no carrinho que devem ser comprados separadamente.
		`;

			this.Notification.show({
				target: document.body,
				method: "appendChild",
				type: "warning",
				message: message,
				last: 7000,
				sticky: true,
			});

			// Log for debugging
			console.warn("[Cart Dropshipping Validator] Mixed cart detected:", {
				dropshipping: dropshippingItems.map((i) => i.product_title),
				regular: regularItems.map((i) => i.product_title),
			});
		}

		/**
		 * Determines which items have increased compared to the stored snapshot
		 * @param {Array} itemsWithTags
		 * @param {Object} response
		 * @returns {Array<{item: Object, previousQuantity: number}>}
		 */
		identifyAddedItems(itemsWithTags, response) {
			const added = [];

			itemsWithTags.forEach((item) => {
				const previous = this.lastCartSnapshot.get(item.key);
				if (!previous) {
					added.push({ item, previousQuantity: 0 });
				} else if (item.quantity > previous.quantity) {
					added.push({ item, previousQuantity: previous.quantity });
				}
			});

			// Fallback: try to pick item based on response metadata (key, line, etc.)
			if (added.length === 0 && response) {
				const candidateKeys = new Set();
				const candidateVariantIds = new Set();

				if (response.key) candidateKeys.add(response.key);
				if (response.id) candidateVariantIds.add(response.id);
				if (response.variant_id) candidateVariantIds.add(response.variant_id);
				if (response.line) {
					const lineIndex = Number(response.line);
					const lineItem = Number.isInteger(lineIndex)
						? itemsWithTags[lineIndex - 1]
						: null;
					if (lineItem) {
						candidateKeys.add(lineItem.key);
					}
				}
				if (response.item?.key) candidateKeys.add(response.item.key);

				if (candidateKeys.size > 0) {
					itemsWithTags.forEach((item) => {
						if (candidateKeys.has(item.key)) {
							const previous = this.lastCartSnapshot.get(item.key);
							added.push({
								item,
								previousQuantity: previous?.quantity || 0,
							});
						}
					});
				} else if (candidateVariantIds.size > 0) {
					itemsWithTags.forEach((item) => {
						if (
							candidateVariantIds.has(item.id) ||
							candidateVariantIds.has(item.variant_id)
						) {
							const previous = this.lastCartSnapshot.get(item.key);
							added.push({
								item,
								previousQuantity: previous?.quantity || 0,
							});
						}
					});
				}
			}

			return added;
		}

		/**
		 * Determines which type (dropshipping or regular) increased since the snapshot
		 * @param {Array} dropshippingItems
		 * @param {Array} regularItems
		 * @returns {"dropshipping"|"regular"|null}
		 */
		resolveNewItemType(dropshippingItems, regularItems) {
			const currentDropQty = dropshippingItems.reduce(
				(total, item) => total + item.quantity,
				0,
			);
			const currentRegularQty = regularItems.reduce(
				(total, item) => total + item.quantity,
				0,
			);

			const prevDropQty = this.snapshotMeta.dropshippingQuantity || 0;
			const prevRegularQty = this.snapshotMeta.regularQuantity || 0;

			const dropIncreased = currentDropQty > prevDropQty;
			const regularIncreased = currentRegularQty > prevRegularQty;

			if (dropIncreased && !regularIncreased) return "dropshipping";
			if (regularIncreased && !dropIncreased) return "regular";
			return null;
		}

		/**
		 * Chooses which items to revert to resolve the conflict
		 * @param {Array} addedItems
		 * @param {"dropshipping"|"regular"|null} newType
		 * @param {Array} itemsWithTags
		 * @param {Array} dropshippingItems
		 * @param {Array} regularItems
		 * @returns {Array<{item: Object, previousQuantity: number}>}
		 */
		selectConflictingItems(
			addedItems,
			newType,
			itemsWithTags,
			dropshippingItems,
			regularItems,
		) {
			const typeToRemove =
				newType ||
				(addedItems.length
					? this.isDropshippingProduct(addedItems[0].item.tags)
						? "dropshipping"
						: "regular"
					: null);

			const matchesType = (item, type) =>
				this.isDropshippingProduct(item.tags) === (type === "dropshipping");

			let candidates = [];

			if (typeToRemove) {
				candidates = addedItems.filter(({ item }) =>
					matchesType(item, typeToRemove),
				);

				if (candidates.length === 0) {
					const pool =
						typeToRemove === "dropshipping" ? dropshippingItems : regularItems;
					candidates = pool
						.map((item) => ({
							item,
							previousQuantity:
								this.lastCartSnapshot.get(item.key)?.quantity || 0,
						}))
						.filter(({ item, previousQuantity }) => {
							return item.quantity > previousQuantity;
						});
				}
			}

			if (candidates.length === 0) {
				candidates = addedItems;
			}

			// Ensure we only revert items whose quantity actually increased
			return candidates.filter(
				({ item, previousQuantity }) => item.quantity > previousQuantity,
			);
		}

		/**
		 * Reverts the quantities of conflicting items back to their previous state
		 * @param {Array<{item: Object, previousQuantity: number}>} items
		 */
		async revertConflictingItems(items) {
			if (!items.length) return;

			const groupedSections = [];
			document.documentElement.dispatchEvent(
				new CustomEvent("cart:grouped-sections", {
					bubbles: true,
					detail: { sections: groupedSections },
				}),
			);

			const sections = [
				...(this.cartDrawer
					?.getSectionsToRender?.()
					.map((section) => section.id) || []),
				...(this.mainCart
					?.getSectionsToRender?.()
					.map((section) => section.section) || []),
				...groupedSections,
			];

			for (const { item, previousQuantity } of items) {
				try {
					await this.sendCartChangeRequest(
						item.key,
						previousQuantity,
						sections,
					);
				} catch (error) {
					console.error("Error reverting item quantity:", error);
				}
			}
		}

		/**
		 * Sends a cart change request for a single line item
		 * @param {string} itemKey
		 * @param {number} quantity
		 * @param {Array<string>} sections
		 */
		async sendCartChangeRequest(itemKey, quantity, sections) {
			const response = await fetch(
				window.FoxThemeSettings.routes.cart_change_url,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({
						id: itemKey,
						quantity,
						sections,
						sections_url: window.location.pathname,
					}),
				},
			);

			if (response.ok) {
				const payload = await response.text();
				const updatedCart = payload ? JSON.parse(payload) : null;
				if (updatedCart) {
					updatedCart.source = "cart-dropshipping-validator";
					window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, updatedCart);
				}
			}
		}

		/**
		 * Stores the latest cart snapshot for future comparisons
		 * @param {Array} items
		 */
		storeCartSnapshot(items) {
			this.lastCartSnapshot.clear();
			let dropshippingQuantity = 0;
			let regularQuantity = 0;

			items.forEach((item) => {
				const isDrop = this.isDropshippingProduct(item.tags);
				this.lastCartSnapshot.set(item.key, {
					quantity: item.quantity,
					isDropshipping: isDrop,
				});

				if (isDrop) {
					dropshippingQuantity += item.quantity;
				} else {
					regularQuantity += item.quantity;
				}
			});

			this.snapshotMeta = {
				dropshippingQuantity,
				regularQuantity,
			};
		}

		/**
		 * Resolves the current cart state from different event payload shapes.
		 * Falls back to /cart.js when items are not provided.
		 * @param {Object} response
		 * @returns {Promise<Object|null>}
		 */
		async resolveCartState(response) {
			const maybeCart = response?.cart?.items?.length
				? response.cart
				: response;

			if (maybeCart?.items?.length) {
				return maybeCart;
			}

			try {
				const res = await fetch("/cart.js", {
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
				});
				if (!res.ok) return null;
				return await res.json();
			} catch (error) {
				console.error("Error fetching cart state:", error);
				return null;
			}
		}
	}

	customElements.define(
		"cart-dropshipping-validator",
		CartDropshippingValidator,
	);
}
